import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef, type GridValueGetterParams } from '@mui/x-data-grid';
import { useCancelOrderMutation, useGetMyOrdersQuery } from 'store/api/orderApi';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'utils/constants';
import WriteReviewPopup from 'components/review/write-review-popup/WriteReviewPopup';
import { formatDualCurrency } from 'utils/currency';
import useWindowWidth from 'hooks/useWindowWidth';
import Pagination from 'components/pagination/Pagination';
import { canBuyerCancel, getBuyerOrderStatus } from 'utils/orderTracking';
import CancelOrderDialog from 'components/cancel-order/CancelOrderDialog';
import { useNotification } from 'components/notification/Notification';

const ProfileOrders = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowWidth();
  const [queryParams, setQueryParams] = useState({ page: DEFAULT_PAGE });
  const { data: orders, isLoading } = useGetMyOrdersQuery(queryParams);
  const { showNotification } = useNotification();
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [cancelError, setCancelError] = useState<boolean>(false);
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);
  const [currentSeller, setCurrentSeller] = useState<{ id: number, name: string }>({
    id: 0,
    name: ''
  });

  const handleReviewClick = (sellerId: number, sellerUsername: string) => {
    setCurrentSeller({
      id: sellerId,
      name: sellerUsername
    });
    setReviewPopupOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelOrderId(null);
    setCancelError(false);
  };

  const confirmCancel = async (reason: string) => {
    if (cancelOrderId === null) return;

    setCancelError(false);
    const result = await cancelOrder({ orderId: cancelOrderId, reason });

    if ('error' in result) {
      // Any refusal (courier already has it, or Speedy declined) gets the
      // same message - never the server's English text.
      setCancelError(true);
      return;
    }

    // The mutation invalidates the 'Orders' tag, so the list refetches and
    // the row flips to "Отказана" without a reload.
    closeCancelDialog();
    showNotification(t('cancelOrder.success'));
  };

  // Buyer-friendly label mapped from Speedy codes; codes without a mapping
  // (failed delivery, returns, ...) keep Speedy's own label.
  const statusLabel = (row: any): string => {
    const status = getBuyerOrderStatus(row);

    return status
      ? t(`orderStatus.${status}`)
      : t(`operationCodes.${row.operationCode.toString()}`);
  };

  // Shown until the courier has picked up/received the parcel (see
  // utils/orderTracking.ts); the server re-checks this and Speedy's cancel
  // response is the final authority.
  const renderActions = (row: any) => canBuyerCancel(row)
    ? (
      <button
        type="button"
        className="cancel-order-btn cancel-order-btn-outlined-danger"
        onClick={() => {
          setCancelOrderId(row.id);
        }}
      >
        {t('cancelOrder.action')}
      </button>
      )
    : null;

  const dataColumnsDesktop: GridColDef[] = [
    {
      field: 'createdOn',
      headerName: t('profile.payments.date'),
      flex: 1,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => {
        return new Date(params.row.createdOn).toLocaleDateString();
      }
    },
    {
      field: 'id',
      flex: 1,
      headerName: t('profile.payments.numberOfOrder'),
      disableColumnMenu: true
    },
    {
      field: 'sellerUsername',
      headerName: t('profile.payments.user'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column">
          {params.row.sellerUsername}
          <span
            className="profile-orders-table-write-review"
            onClick={() => {
              handleReviewClick(params.row.sellerId, params.row.sellerUsername);
            }}
          >
            {t('profile.payments.writeReview')}
          </span>
        </div>
      )
    },
    {
      field: 'totalPrice',
      headerName: t('profile.payments.paidAmount'),
      disableColumnMenu: true,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        return formatDualCurrency(params.row.totalPrice.amount, params.row.totalPrice.currency);
      }
    },
    {
      field: 'status',
      headerName: t('profile.payments.status'),
      disableColumnMenu: true,
      width: 200,
      valueGetter: (params: GridValueGetterParams) => statusLabel(params.row)
    },
    {
      field: 'actions',
      headerName: t('cancelOrder.actions'),
      disableColumnMenu: true,
      sortable: false,
      width: 260,
      renderCell: (params) => renderActions(params.row)
    }
  ];

  const dataColumnsMobile: GridColDef[] = [
    {
      field: 'createdOn',
      headerName: t('profile.payments.date'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column justify-center align-center">
          <span className="profile-orders-table-order-id">№ {params.row.id}</span>
          {new Date(params.row.createdOn).toLocaleDateString()}
        </div>
      )
    },
    {
      field: 'sellerUsername',
      headerName: t('profile.payments.user'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column justify-center align-center">
          {params.row.sellerUsername}
          <span
            className="profile-orders-table-write-review"
            onClick={() => {
              handleReviewClick(params.row.sellerId, params.row.sellerUsername);
            }}
          >
            {t('profile.payments.writeReview')}
          </span>
        </div>
      )
    },
    {
      field: 'totalPrice',
      headerName: t('profile.payments.paidAmount'),
      flex: 1,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => {
        return formatDualCurrency(params.row.totalPrice.amount, params.row.totalPrice.currency);
      }
    },
    {
      field: 'status',
      headerName: t('profile.payments.status'),
      disableColumnMenu: true,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => statusLabel(params.row)
    },
    {
      field: 'actions',
      headerName: t('cancelOrder.actions'),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      renderCell: (params) => renderActions(params.row)
    }
  ];

  if (isLoading) {
    return <div>{t('profile.payments.loadingOrders')}</div>;
  }

  if (orders?.models.length === 0) {
    return <div>{t('profile.payments.noOrders')}</div>;
  }

  return (
    <div>
      <DataGrid
        className="profile-data-grid profile-orders-table"
        rows={orders?.models ?? []}
        columns={isMobile ? dataColumnsMobile : dataColumnsDesktop}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE }
          }
        }}
        getRowHeight={() => 'auto'}
        pageSizeOptions={[DEFAULT_PAGE_SIZE]}
        disableColumnFilter
        rowCount={orders?.totalCount ?? 0}
        paginationMode='server'
        onPaginationModelChange={(e) => {
          setQueryParams({ page: e.page + 1 });
        }}
      />
      <Pagination
        count={orders?.totalPages ?? 1}
        page={queryParams.page}
        onChange={(value) => {
          setQueryParams({ page: value });
        }}
      />
      <CancelOrderDialog
        open={cancelOrderId !== null}
        orderId={cancelOrderId}
        mode="buyer"
        isLoading={isCancelling}
        hasError={cancelError}
        onClose={closeCancelDialog}
        onConfirm={(reason) => {
          void confirmCancel(reason);
        }}
      />
      {reviewPopupOpen && (
        <WriteReviewPopup
          sellerId={currentSeller.id}
          sellerUsername={currentSeller.name}
          open={reviewPopupOpen}
          onClose={() => {
            setReviewPopupOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileOrders;
